import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, MicOff, Headphones, HeadphoneOff, Video, VideoOff, Phone, PhoneOff, MonitorUp, UserPlus, Settings2, Hand, Disc, Lock, Unlock, Loader2, Eye, EyeOff, Minimize2, Home, MoreHorizontal, Focus, BarChart3 } from 'lucide-react'
import { useIsMobile } from '../../hooks/useBreakpoint'
import PhoneDialModal from './PhoneDialModal'
import LaunchEventMenu from './LaunchEventMenu'
import AudioDeviceSelector from './AudioDeviceSelector'
import AudioOutputDeviceSelector from './AudioOutputDeviceSelector'
import VideoDeviceSelector from './VideoDeviceSelector'
import VoiceVideoSettingsButton from './VoiceVideoSettingsButton'
import SoundboardPicker from './SoundboardPicker'
import ReactionsPicker from './ReactionsPicker'
// OSPROD-134: BackgroundEffectPicker / CameraZoomPicker / AutoFaceCenterButton
// no longer appear as standalone toolbar buttons — they're now mounted
// inside the Camera dropdown (VideoDeviceSelector's settings row).
import RecordingsList from './RecordingsList'
import MicStuckBanner from './MicStuckBanner'
import { mediaManager, type DenoiserStatus, onScreenShareStarted, onScreenShareStopped } from '../../services/mediaManager'
import { screenOverlayClient } from '../../services/screenOverlayClient'
import { getSocket } from '../../services/socket'
import { useVoiceStore } from '../../stores/voiceStore'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { homeChannelExists } from './homeChannel'
import { useLivekitStore } from '../../stores/livekitStore'
import { useAppShellStore } from '../../stores/appShellStore'
import { useGuestStore } from '../../stores/guestStore'
import { useRecordingStore } from '@os/spaces/web/stores/recordingStore'
import { channelApi } from '../../services/api'

interface Props {
  isMuted: boolean
  isDeafened: boolean
  isScreenSharing: boolean
  isVideoOn: boolean
  /** When true, the local audio track is actually published and live (ground truth) */
  isAudioTrackReady?: boolean
  /** When true, the local video track is actually published and live (ground truth) */
  isVideoTrackReady?: boolean
  /** When true, the local screen share track is actually published and live (ground truth) */
  isScreenTrackReady?: boolean
  onToggleMute: () => void
  onToggleDeafen: () => void
  onToggleScreenShare: () => void
  onToggleVideo: () => void
  onDisconnect: () => void
  channelId?: string
  onOpenInvite?: () => void
  /** Whether this user has voice-host rights — gates Record + Lock. */
  isHost?: boolean
  /** Whether the channel is currently locked (waiting room mode). */
  isLocked?: boolean
  onToggleLock?: () => void
  /** Display name of another participant who is currently sharing
   * their screen. When set (and we're not the one sharing), the
   * Share-Screen button is disabled — only one participant can share
   * at a time, like Zoom. */
  screenShareLockedBy?: string | null
}

export default function VoiceChannelControls({
  isMuted,
  isDeafened,
  isScreenSharing,
  isVideoOn,
  isAudioTrackReady,
  isVideoTrackReady,
  isScreenTrackReady,
  onToggleMute,
  onToggleDeafen,
  onToggleScreenShare,
  onToggleVideo,
  onDisconnect,
  onOpenInvite,
  channelId,
  isHost,
  isLocked,
  onToggleLock,
  screenShareLockedBy,
}: Props) {
  const [showPhoneDial, setShowPhoneDial] = useState(false)
  const [showLaunch, setShowLaunch] = useState(false)
  // OSPROD-MOBILE-1c: on a phone the 12+ utility buttons wrapped to 3 dense
  // rows. Collapse the secondary controls behind a "More" toggle so the
  // default bar is one compact row; desktop (>=lg) shows everything inline.
  const isMobile = useIsMobile()
  const [moreOpen, setMoreOpen] = useState(false)
  // OSPROD-160: guests get the full room view but without host/owner
  // affordances — hide invite, lock, recording (+download) and dial-in.
  const isGuest = useGuestStore((s) => s.isGuest)

  // OSPROD-63: collapse the call into the global ActiveCallBar mini-
  // window. The LiveKit room stays connected (livekitStore is a
  // singleton); only the full-screen voice surface unmounts.
  //
  // History:
  //   v1 — navigated to `/desktop` (route never existed → black screen).
  //   v2 — navigated to `/channels/@me` AND set section='desktop' so
  //        Chat.tsx rendered the Dashboards surface. QA flagged this:
  //        users were force-parked on the Desktop dashboard regardless
  //        of where they came from.
  //   v3 (here) — restore the user to wherever they were before they
  //        joined the call (mail, tracker, a text channel, a DM, etc.)
  //        using LastNonCallPathTracker's recorded path. Fallback chain
  //        intentionally avoids 'desktop' — section='chats' + DM list
  //        is the neutral landing when no prior path exists.
  const navigate = useNavigate()
  // Home voice channel — a per-user single "default" room. When set, a house
  // button shows before the mic; clicking it jumps straight there (parks the
  // one-shot auto-join intent VoiceChannelView consumes on mount, then
  // navigates — the same path the sidebar uses to join a room). Set/unset from
  // a voice channel's context menu in the sidebar.
  const homeChannelId = useAuthStore((s) => (s.user?.settings as { homeVoiceChannelId?: string } | undefined)?.homeVoiceChannelId)
  // "Видео только говорящих" — receive cameras only for recent speakers
  // (VoiceManager does the SFU unsubscription; this is just the toggle).
  const videoSpeakersOnly = useVoiceStore((s) => s.videoSpeakersOnly)
  const setVideoSpeakersOnly = useVoiceStore((s) => s.setVideoSpeakersOnly)
  const homeServerId = useAuthStore((s) => (s.user?.settings as { homeVoiceServerId?: string } | undefined)?.homeVoiceServerId)
  // The home channel may have been deleted (or the user lost access) after it
  // was set — settings.homeVoiceChannelId then dangles. Only surface the house
  // button while the target still exists in the loaded server list, otherwise
  // the jump lands on a dead /servers/.../channels/<gone> route ("nowhere").
  const servers = useServerStore((s) => s.servers)
  const homeChannelLive = useMemo(() => homeChannelExists(servers, homeChannelId), [servers, homeChannelId])
  const goHome = () => {
    if (!homeChannelId || !homeChannelLive) return
    useVoiceStore.getState().setPendingAutoJoin(homeChannelId)
    navigate(`/servers/${homeServerId ?? '@me'}/channels/${homeChannelId}`)
  }
  const setSection = useAppShellStore((s) => s.setSection)
  const lastNonCallPath = useAppShellStore((s) => s.lastNonCallPath)
  const lastChatPath = useAppShellStore((s) => s.lastChatPath)

  const handleMinimize = () => {
    // Primary: drop the user back where they came from. The tracker
    // in App.tsx skips voice-channel URLs, so this can't loop back
    // into the call we're trying to minimize.
    if (lastNonCallPath) {
      navigate(lastNonCallPath)
      return
    }
    // Deep-link / first session — pick a neutral surface. NOT the
    // Desktop dashboard (QA-asked); the DM list is the closest thing
    // to "wherever the user would have been if they'd opened the app
    // normally." section='chats' makes Chat.tsx render TelegramChatView
    // rather than falling into the empty ChannelChatView branch.
    if (lastChatPath) {
      setSection('chats')
      navigate(lastChatPath)
      return
    }
    setSection('chats')
    navigate('/channels/@me')
  }
  // Live denoiser state, so the mic "connecting" spinner persists until the
  // denoiser is actually ACTIVE — not just until the track is published.
  // There's a ~2-3s window where the mic is published (isAudioTrackReady=true)
  // but the denoiser is still 'idle' (attaching); during it the user isn't
  // effectively heard, so the spinner must keep spinning until denoiser !==
  // 'idle' ('off'/'browser'/'rnnoise'/'dfn'/'os-*' all count as ready).
  const [denoiser, setDenoiser] = useState<DenoiserStatus>(mediaManager.getDenoiserState())
  useEffect(() => mediaManager.subscribeDenoiser(setDenoiser), [])
  // "Connecting": mic ON but either not yet published OR denoiser still
  // attaching. Drives the spinner / amber tint / title on the mic button.
  const showMicWarning = !isMuted && (isAudioTrackReady === false || denoiser === 'idle')
  const showVideoWarning = isVideoTrackReady === false && isVideoOn
  const showScreenWarning = isScreenTrackReady === false && isScreenSharing
  const myIdentity = useLivekitStore((s) => s.room?.localParticipant?.identity)
  const isHandRaised = useVoiceStore((s) => (myIdentity ? s.raisedHands.has(myIdentity) : false))
  const toggleLocalRaisedHand = useVoiceStore((s) => s.toggleLocalRaisedHand)
  // OSPROD-57: while a toggle is in flight, gate the button so a second
  // click can't fire a competing publish.
  const micPending = useLivekitStore((s) => s.micToggleInFlight)
  const videoPending = useLivekitStore((s) => s.videoToggleInFlight)
  const screenPending = useLivekitStore((s) => s.screenToggleInFlight)
  // OSPROD-60: local-only "Hide self-view" toggle. Camera publish is
  // unaffected — only the local self-tile gets filtered out of the grid.
  const isSelfViewHidden = useVoiceStore((s) => s.isSelfViewHidden)
  const setSelfViewHidden = useVoiceStore((s) => s.setSelfViewHidden)
  const activeRecording = useRecordingStore((s) => (channelId ? s.active[channelId] : null))
  const startRecording = useRecordingStore((s) => s.start)
  const stopRecording = useRecordingStore((s) => s.stop)
  const isRecording = !!activeRecording && (activeRecording.status === 'ACTIVE' || activeRecording.status === 'STARTING')

  const onToggleRecording = async () => {
    if (!channelId) return
    if (isRecording && activeRecording) {
      await stopRecording(activeRecording)
    } else {
      await startRecording(channelId)
    }
  }

  const onToggleLockInternal = async () => {
    if (!channelId) return
    if (onToggleLock) return onToggleLock()
    try {
      await channelApi.setLocked(channelId, !isLocked)
    } catch (err) {
      console.warn('Lock toggle failed', err)
    }
  }

  // Screen-share annotation overlay: open when this participant starts sharing,
  // close + wipe annotations (draw:clear) when they stop.
  //
  // **WEB INTENTIONALLY HAS NO PiP / SEPARATE WINDOW.** A floating Picture-in-
  // Picture overlay isn't actually useful for screen-share annotation — you
  // can't see-through it to your real desktop, and a "mirror window" just
  // duplicates the in-app preview tile (which already carries the full Zoom
  // toolbar post-1.1.209A refactor). True desktop overlay above OS chrome
  // is a browser-security impossibility (no transparent fullscreen, no
  // click-through APIs). Zoom and Google Meet web don't do it either —
  // their annotations live inside the web tab. For real-desktop overlay,
  // users need the Electron app.
  //
  // Electron path stays: IPC to a transparent fullscreen BrowserWindow on
  // the captured monitor with click-through default + Annotate mode.
  useEffect(() => {
    if (!channelId) return
    const isElectron =
      typeof window !== 'undefined' &&
      !!window.electronAPI?.screenOverlay

    const offStart = onScreenShareStarted(({ trackSid, bounds }) => {
      if (!trackSid) return
      if (isElectron) {
        screenOverlayClient.open({ channelId, sessionId: trackSid, bounds }).catch((err) => {
          console.error('[VoiceChannelControls] electron overlay open failed', err)
        })
      }
      // Web: nothing. In-app preview tile in VoiceChannelView carries
      // ScreenShareAnnotation with the full toolset.
    })

    const offStop = onScreenShareStopped(({ trackSid }) => {
      if (isElectron) {
        screenOverlayClient.close().catch((err) => {
          console.error('[VoiceChannelControls] electron overlay close failed', err)
        })
      }
      // Wipe annotations across all viewers (Zoom-style). This still fires
      // on web so the in-app preview tile + viewer canvases blank on stop.
      if (trackSid) {
        try {
          getSocket().emit('draw:clear', { channelId, sessionId: trackSid, system: true })
        } catch (err) {
          console.error('[VoiceChannelControls] draw:clear emit failed', err)
        }
      }
    })
    return () => {
      offStart()
      offStop()
    }
  }, [channelId])

  return (
    // Mobile: wrap the control row instead of overflowing a single line —
    // a non-wrapping flex centred the bar so on a phone (~430px) BOTH ends
    // (Mic on the left, Disconnect on the right) were clipped off-screen.
    // flex-wrap keeps every button reachable (2 rows on a phone, still one
    // row wherever it fits). pb honours the iPhone home-indicator safe area.
    <div
      className="flex flex-wrap items-center justify-center gap-2 px-2 pt-3 lg:gap-3 lg:px-0 bg-card border-t border-border"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
    >
      {/* Escalates a SUSTAINED stuck mic (unmuted-intent but no live audio track)
          into an actionable in-place Reconnect — saves the full-page refresh the
          25-person 2026-06-29 standup had to do. w-full → own row above the bar. */}
      <MicStuckBanner />
      {homeChannelLive && (
        <ControlButton
          active={false}
          activeColor="bg-muted text-foreground hover:bg-muted/80"
          inactiveColor="bg-muted text-foreground hover:bg-muted/80"
          onClick={goHome}
          title="Go to your home channel"
        >
          <Home size={20} />
        </ControlButton>
      )}
      <div className="relative">
        <ControlButton
          active={isMuted}
          activeColor="bg-destructive/20 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          inactiveColor={showMicWarning ? 'bg-yellow-500/20 text-yellow-500 hover:text-yellow-400' : 'bg-muted text-foreground hover:bg-muted/80'}
          onClick={onToggleMute}
          pending={micPending}
          title={micPending ? 'Updating microphone…' : showMicWarning ? 'Микрофон подключается (шумоподавление)… нажми, чтобы повторить' : isMuted ? 'Unmute' : 'Mute'}
        >
          {/* showMicWarning = mic ON but not yet published (denoiser still
              attaching, or the denoiser-required gate is retrying). Show an
              animated spinner instead of a dead MicOff so it reads as
              "connecting", not "broken" — the big-call "почему меня не
              слышно" confusion. Button stays clickable (spinner is the icon,
              not the disabling `pending` state) so the user can still
              mute/retry. */}
          {isMuted ? <MicOff size={20} /> : showMicWarning ? <Loader2 size={20} className="animate-spin" /> : <Mic size={20} />}
        </ControlButton>
        <AudioDeviceSelector />
        {/* Denoiser chip (DenoiserBadge) removed 2026-05-22 — now that RNNoise
            is the default for everyone, the "RNN" label over the mic confused
            users ("что это?"). Kept the component file for debugging; just not
            rendered in the control bar. Backend is still readable from the
            [voiceDiagnostic] / [MediaManager] console logs + voice_diag. */}
      </div>

      <div className="relative">
        <ControlButton
          active={isVideoOn}
          activeColor="bg-muted text-foreground hover:bg-muted/80"
          inactiveColor={showVideoWarning ? 'bg-yellow-500/20 text-yellow-500 hover:text-yellow-400' : 'bg-muted text-foreground hover:bg-muted/80'}
          onClick={onToggleVideo}
          pending={videoPending}
          title={videoPending ? 'Updating camera…' : showVideoWarning ? 'Camera not ready — click to retry' : isVideoOn ? 'Turn off camera' : 'Turn on camera'}
        >
          {isVideoOn && !showVideoWarning ? <Video size={20} /> : <VideoOff size={20} />}
        </ControlButton>
        <VideoDeviceSelector />
      </div>

      {/* "Видео только говорящих" (2026-06-12 big-call request): with this
          ON the client receives camera video ONLY from whoever spoke in the
          last few seconds — everyone else's stream is unsubscribed at the
          SFU, so receive bandwidth + decode CPU scale with speakers, not
          room size. Screen share always stays visible. */}
      <ControlButton
        active={videoSpeakersOnly}
        activeColor="bg-primary/20 text-primary hover:bg-primary/30"
        inactiveColor="bg-muted text-foreground hover:bg-muted/80"
        onClick={() => setVideoSpeakersOnly(!videoSpeakersOnly)}
        title={videoSpeakersOnly
          ? 'Speakers-only video is ON — receiving video just from active speakers. Click to show everyone.'
          : 'Show video only from active speakers (saves bandwidth and CPU in big calls)'}
      >
        <Focus size={20} />
      </ControlButton>

      {/* Screen-share button. Zoom-style "one screen at a time" — when
          someone else is already sharing, the button is disabled with
          a tooltip naming them. The local-sharing case is unaffected
          (we still own the toggle). */}
      {(() => {
        const lockedByOther = !!screenShareLockedBy && !isScreenSharing
        return (
          <ControlButton
            active={isScreenSharing}
            activeColor={showScreenWarning ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-600 text-white hover:bg-green-700'}
            inactiveColor={
              lockedByOther
                ? 'bg-muted/50 text-muted-foreground cursor-not-allowed opacity-50'
                // OSPROD-96: green-tint the idle screen-share button so it
                // stands out from the neutral controls (Zoom-style), making
                // it easy to find without squinting at the icon row.
                : 'bg-green-600/15 text-green-500 hover:bg-green-600/25'
            }
            onClick={lockedByOther ? () => {} : onToggleScreenShare}
            pending={screenPending}
            title={
              screenPending
                ? 'Updating screen share…'
                : lockedByOther
                  ? `${screenShareLockedBy} is sharing their screen. Wait until they stop.`
                  : showScreenWarning
                    ? 'Screen share not ready — click to retry'
                    : isScreenSharing
                      ? 'Stop Sharing'
                      : 'Share Screen'
            }
          >
            <MonitorUp size={20} />
          </ControlButton>
        )
      })()}

      <div className="relative">
        <ControlButton
          active={isDeafened}
          activeColor="bg-destructive/20 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          inactiveColor="bg-muted text-foreground hover:bg-muted/80"
          onClick={onToggleDeafen}
          title={isDeafened ? 'Undeafen' : 'Deafen'}
        >
          {isDeafened ? <HeadphoneOff size={20} /> : <Headphones size={20} />}
        </ControlButton>
        <AudioOutputDeviceSelector />
      </div>

      {/* MasterVolumeControl was a standalone button + popover slider here.
          Consolidated into the AudioOutputDeviceSelector dropdown in 1.1.180A
          (item #6 of the 2026-05-26 list — user-requested symmetry with the
          MicInputVolumeSlider inside AudioDeviceSelector). The volume knob
          now lives next to the playback device picker, attached to the
          deafen button's ChevronUp badge.

          OSPROD-134: CameraZoomPicker / AutoFaceCenterButton /
          BackgroundEffectPicker also folded — they now live inside the
          Camera dropdown (VideoDeviceSelector's settings row, under the
          camera ChevronUp). The main toolbar keeps only the primary
          mic / cam / share / speaker controls + utilities below. */}

      {/* Mobile: collapse the secondary utilities behind a "More" toggle so
          the default bar is one compact row (mic/cam/share/speaker/more/
          disconnect). Desktop (>=lg) renders everything inline as before. */}
      {isMobile && (
        <button
          onClick={() => setMoreOpen((v) => !v)}
          title={moreOpen ? 'Fewer controls' : 'More controls'}
          aria-expanded={moreOpen}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition duration-150 ${moreOpen ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground hover:bg-muted/80'}`}
        >
          <MoreHorizontal size={20} />
        </button>
      )}
      {(!isMobile || moreOpen) && (
        <>
      <VoiceVideoSettingsButton />

      <ControlButton
        active={isSelfViewHidden}
        activeColor="bg-primary/15 text-primary hover:bg-primary/25"
        inactiveColor="bg-muted text-foreground hover:bg-muted/80"
        onClick={() => setSelfViewHidden(!isSelfViewHidden)}
        title={isSelfViewHidden ? 'Show self-view' : 'Hide self-view (others still see you)'}
      >
        {isSelfViewHidden ? <EyeOff size={20} /> : <Eye size={20} />}
      </ControlButton>

      {!isGuest && isHost && channelId && (
        <ControlButton
          active={isRecording}
          activeColor="bg-red-600 text-white hover:bg-red-700"
          inactiveColor="bg-muted text-foreground hover:bg-muted/80"
          onClick={onToggleRecording}
          title={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? <Disc size={20} className="animate-pulse" /> : <Disc size={20} />}
        </ControlButton>
      )}

      {!isGuest && channelId && <RecordingsList channelId={channelId} />}

      {/* Live events: launch a poll or synchronized presentation. Host-only. */}
      {!isGuest && isHost && channelId && (
        <div className="relative">
          <ControlButton
            active={showLaunch}
            activeColor="bg-primary text-primary-foreground hover:opacity-90"
            inactiveColor="bg-muted text-foreground hover:bg-muted/80"
            onClick={() => setShowLaunch((v) => !v)}
            title="Start poll / presentation"
          >
            <BarChart3 size={20} />
          </ControlButton>
          {showLaunch && (
            <LaunchEventMenu channelId={channelId} onClose={() => setShowLaunch(false)} />
          )}
        </div>
      )}

      {!isGuest && isHost && channelId && (
        <ControlButton
          active={!!isLocked}
          activeColor="bg-amber-500/90 text-white hover:bg-amber-600"
          inactiveColor="bg-muted text-foreground hover:bg-muted/80"
          onClick={onToggleLockInternal}
          title={isLocked ? 'Unlock room (admit everyone)' : 'Lock room (waiting room on)'}
        >
          {isLocked ? <Lock size={20} /> : <Unlock size={20} />}
        </ControlButton>
      )}

      <ControlButton
        active={isHandRaised}
        activeColor="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30"
        inactiveColor="bg-muted text-foreground hover:bg-muted/80"
        onClick={toggleLocalRaisedHand}
        title={isHandRaised ? 'Lower hand' : 'Raise hand'}
      >
        <Hand size={20} />
      </ControlButton>

      <ReactionsPicker />

      <SoundboardPicker />

      <VideoQualityPicker />

      {/* Channel-invite (room link) is open to every member — anyone in
          the call can pull a colleague in. The user-invite (registration)
          path on the server is the one that stays admin-only. */}
      {!isGuest && (
        <button
          onClick={onOpenInvite}
          className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150"
          title="Invite people"
        >
          <UserPlus size={20} />
        </button>
      )}

      {/* OSPROD-88: dial an external phone number into the voice room.
          Only the host has authority to expose the room over PSTN. */}
      {!isGuest && isHost && channelId && (
        <button
          onClick={() => setShowPhoneDial(true)}
          className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150"
          title="Invite by phone"
        >
          <Phone size={20} />
        </button>
      )}
      {showPhoneDial && channelId && (
        <PhoneDialModal channelId={channelId} onClose={() => setShowPhoneDial(false)} />
      )}

      {/* OSPROD-63: collapse into the global mini-window. The LiveKit
          room stays connected (livekitStore is a singleton); only the
          full-screen voice surface unmounts and ActiveCallBar (mounted
          in AppLayout) becomes the floating PiP.

          Earlier this handler did `setSection('desktop')` + navigate
          `/channels/@me`, which force-parked every user on the Desktop
          dashboard regardless of what they were doing before the call
          (QA: "переход на вкладку Desktop происходить не должен").
          Now we restore the most recent non-call path the
          LastNonCallPathTracker (in App.tsx) recorded — so a user who
          jumped into voice from a Tracker page, mail, a text channel,
          or a DM is dropped back exactly where they were. Fallbacks
          stay sensible (chats list, never Desktop) when there is no
          captured prior path (deep link / first session). */}
      <button
        onClick={handleMinimize}
        className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150"
        title="Minimize call to mini-window"
      >
        <Minimize2 size={20} />
      </button>
        </>
      )}

      <button
        onClick={onDisconnect}
        className="w-10 h-10 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/80 flex items-center justify-center transition duration-150"
        title="Disconnect"
      >
        <PhoneOff size={20} />
      </button>
    </div>
  )
}

function ControlButton({
  active,
  activeColor,
  inactiveColor,
  onClick,
  title,
  children,
  pending,
}: {
  active: boolean
  activeColor: string
  inactiveColor: string
  onClick: () => void
  title: string
  children: React.ReactNode
  /**
   * When true the button is visually busy (spinner + lower opacity) and
   * `onClick` is suppressed. The store-level guard already drops
   * concurrent calls, but disabling at the DOM level prevents the user
   * from queuing repeated optimistic flips that briefly flash the UI.
   */
  pending?: boolean
}) {
  return (
    <button
      onClick={pending ? undefined : onClick}
      disabled={pending}
      aria-busy={pending || undefined}
      title={title}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition duration-150 ${
        active ? activeColor : inactiveColor
      } ${pending ? 'opacity-60 cursor-wait' : ''}`}
    >
      {pending ? <Loader2 size={18} className="animate-spin" /> : children}
    </button>
  )
}

// Outgoing presets — control what *I* publish (uplink). VP9 SVC: a
// single encoded stream with three embedded spatial layers; the SFU
// drops layers per subscriber based on their downlink and tile size.
// Bitrates are tuned for "quality first" (Chrome/Electron-only platform,
// hardware VP9 decode is universal); a weaker uplink bumps the user
// down to HD or Low manually.
const QUALITY_OPTIONS: Array<{ value: 'auto' | 'low' | 'hd' | 'fhd'; label: string; hint: string }> = [
  { value: 'fhd', label: 'Full HD 1080p', hint: 'Max quality, ~8 Mbps uplink' },
  { value: 'hd', label: 'HD 720p', hint: 'Balanced, ~4 Mbps uplink' },
  { value: 'low', label: 'Low 180p', hint: 'Bandwidth-saver, ~250 kbps uplink' },
]

// Incoming cap — what's the *highest* simulcast layer I'm willing to
// receive from each peer. 'auto' lets the SFU pick the top layer my
// downlink supports (with bandwidth-aware fallback). Manual options are
// for users on weak downlinks (e.g. 1.5 Mbps) who need to hard-cap so
// they can sit in a multi-peer call without thrashing.
const INCOMING_OPTIONS: Array<{ value: 'auto' | 'medium' | 'low'; label: string; hint: string }> = [
  { value: 'auto', label: 'Auto', hint: 'Highest layer my downlink can carry' },
  { value: 'medium', label: 'Medium 540p', hint: 'Cap at ~800 kbps per peer' },
  { value: 'low', label: 'Low 180p', hint: 'Cap at ~150 kbps per peer' },
]

function VideoQualityPicker() {
  const videoQuality = useVoiceStore((s) => s.videoQuality)
  const setVideoQuality = useVoiceStore((s) => s.setVideoQuality)
  const incomingQuality = useVoiceStore((s) => s.incomingQuality)
  const setIncomingQuality = useVoiceStore((s) => s.setIncomingQuality)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={`Send: ${videoQuality} · Receive: ${incomingQuality}. Click to change.`}
        className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150"
      >
        <Settings2 size={20} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-64 bg-card rounded-lg ring-1 ring-border shadow-lg overflow-hidden z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
            My video — send
          </div>
          {QUALITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setVideoQuality(opt.value) }}
              className={`w-full text-left px-3 py-2 hover:bg-accent transition flex items-start gap-2 ${
                videoQuality === opt.value ? 'bg-primary/10' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${videoQuality === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight">{opt.hint}</div>
              </div>
              {videoQuality === opt.value && (
                <span className="text-primary text-sm leading-none mt-0.5">✓</span>
              )}
            </button>
          ))}
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-y border-border">
            Receive — downlink cap
          </div>
          {INCOMING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setIncomingQuality(opt.value) }}
              className={`w-full text-left px-3 py-2 hover:bg-accent transition flex items-start gap-2 ${
                incomingQuality === opt.value ? 'bg-primary/10' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${incomingQuality === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight">{opt.hint}</div>
              </div>
              {incomingQuality === opt.value && (
                <span className="text-primary text-sm leading-none mt-0.5">✓</span>
              )}
            </button>
          ))}
          <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
            Send change blinks the camera ~200ms. Receive applies instantly.
          </div>
        </div>
      )}
    </div>
  )
}
